import { ICookies } from '@framework/router/Next/cookie/ClientCookie';
import Logger from '@framework/sentry/Logger';

export interface IClientReqData {
  ssid?: string,
  xFinsightClientIp?: string,
  uberTraceId?: string,
  userAgent?: string,
  currentHost?: string,
  referer?: string,
  host?: string,
  via?: string,
  cookie?: string,
  fingerprint?: string,
  serverName?: string,

  [key: string]: string | undefined,
}

export interface IFromRequestProps {
  headers: IClientReqData,
  cookies: ICookies,
}

class ClientReqData {
  constructor(private _headers: IClientReqData) {}

  public static fromRequest({ headers, cookies }: IFromRequestProps, serverName: string = ''): ClientReqData {
    const { ssid, fingerprint = null } = cookies;
    const {
      'x-finsight-client-ip': xFinsightClientIp,
      'uber-trace-id': uberTraceId,
      'user-agent': userAgent,
      host: currentHost,
      referer = '',
      ...otherHeaders
    } = headers;

    return new ClientReqData({
      ssid,
      xFinsightClientIp,
      uberTraceId,
      currentHost,
      userAgent,
      // @ts-ignore TODO: FIN-32798 strictNullChecks
      fingerprint,
      referer,
      serverName,
      ...otherHeaders,
    });
  }

  public static async fromNextHeader(): Promise<ClientReqData> {
    // eslint-disable-next-line global-require
    const { cookies, headers } = require('next/headers');
    const headersList = await headers();
    const cookieStore = await cookies();

    const requestData: { cookies: Record<string, string>, headers: Record<string, string> } = {
      cookies: {},
      headers: {},
    };

    cookieStore.getAll().forEach((cookie: { name: string, value: string }) => {
      requestData.cookies[cookie.name] = cookie.value;
    });
    headersList.forEach((value: string, key: string) => {
      requestData.headers[key] = value;
    });

    const serverName = ClientReqData.getServerHostName();
    return ClientReqData.fromRequest(requestData, serverName);
  }

  public static getServerHostName() {
    // eslint-disable-next-line global-require
    const osModule = require('os');
    try {
      return osModule.hostname();
    } catch (error) {
      Logger.error({ message: 'Hostname not detected', error });
      return 'hostname not detected';
    }
  }

  get headers(): IClientReqData {
    return this._headers;
  }

  get ssrHeaders(): Headers {
    const {
      xFinsightClientIp,
      uberTraceId,
      userAgent,
      serverName,
      cookie,
      referer,
      currentHost,
      via: clientVia,
      'x-geoip-country-code': geoIpCountryCode,
      'x-geoip-region': geoIpRegion,
      'x-geoip-region-code': geoIpRegionCode,
      'x-geoip-city': geoIpCity,
      'x-geoip-latitude': geoIpLatitude,
      'x-geoip-longitude': geoIpLongitude,
      'x-test-request': testRequest,
      'x-test-case-id': testCaseId,
      'x-test-run-id': testRunId,
    } = this.headers;

    const headers = new Headers();
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('Host', currentHost);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('User-Agent', userAgent);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('X-Finsight-Client-IP', xFinsightClientIp);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('Uber-Trace-Id', uberTraceId);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('X-Referer', referer);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('Referer', referer);
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('Cookie', cookie);

    // The outbound RPC call is a second hop through the same CDN/ingress that already
    // resolved these from the browser's own request to this server — without forwarding
    // them, that second hop would re-resolve geo from this server's own IP instead.
    geoIpCountryCode && headers.append('X-GeoIP-Country-Code', geoIpCountryCode);
    geoIpRegion && headers.append('X-GeoIP-Region', geoIpRegion);
    geoIpRegionCode && headers.append('X-GeoIP-Region-Code', geoIpRegionCode);
    geoIpCity && headers.append('X-GeoIP-City', geoIpCity);
    geoIpLatitude && headers.append('X-GeoIP-Latitude', geoIpLatitude);
    geoIpLongitude && headers.append('X-GeoIP-Longitude', geoIpLongitude);

    testRequest && headers.append('X-Test-Request', testRequest);
    testCaseId && headers.append('X-Test-Case-Id', testCaseId);
    testRunId && headers.append('X-Test-Run-Id', testRunId);

    const newViaHeaders = clientVia ? `${ clientVia }, ${ serverName }` : serverName;
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    headers.append('Via', newViaHeaders);
    return headers;
  }

  // @ts-ignore TODO: FIN-32798 strictNullChecks
  get userAgent(): string {
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    return this._headers?.userAgent;
  }

  // @ts-ignore TODO: FIN-32798 strictNullChecks
  get currentHost(): string {
    // @ts-ignore TODO: FIN-32798 strictNullChecks
    return this._headers?.currentHost;
  }
}

export default ClientReqData;
